import { Key, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineDelete, MdOutlineEdit, MdOutlineMoreHoriz } from 'react-icons/md';
import { TmdxAvatar, TmdxChip } from '@ds';
import { ActionMenu, MenuItemType, TableColumnType, TableSwitch } from '@ui';
import { OcsConsoleListFilters, OcsConsoleData, OcsConsoleStatus } from '@/api/apis/resources';
import { getColorsByOrganType, ORGAN_ICON } from '@/utils/helpers';
import { ocsConsoleStatusName } from '@/constants/resources.constants';
import { OrganType } from '@/api/apis/case';
import { col, COMMON_COLUMN_PRESETS } from '../../utils';

export enum MaintenanceStatus {
  All = 'All',
  Maintenance = 'Maintenance',
  Operational = 'Operational',
}

export const OCS_CONSOLES_LIST_FILTERS_BASE: OcsConsoleListFilters = {
  organType: [],
  nopHub: [],
  ocsConsoleStatus: [],
  trialProgramIds: [],
  isInMaintenance: undefined,
  trialProgramsFilterType: undefined,
};

export const FILTERS_NAMES: Record<string, string> = {
  organType: 'Organ',
  nopHub: 'NOP Hub',
  ocsConsoleStatus: 'Status',
  trialProgramIds: 'Trial Programs',
  isInMaintenance: 'Maintenance',
};

export const OCS_CONSOLES_TABLE_COLUMNS: TableColumnType[] = [
  col('ocsConsoleName', 'OCS Console Name', {
    sortable: false,
    minWidthClassName: 'min-w-[220px]',
  }),
  col('organType', 'Organ', {
    sortable: false,
    minWidthClassName: 'min-w-[80px]',
  }),
  col('trialProgramName', 'Trial Program', {
    sortable: true,
    minWidthClassName: 'min-w-[120px]',
  }),
  col('serialNumber', 'Serial Number', {
    sortable: true,
    minWidthClassName: 'min-w-[190px]',
  }),
  col('nopHub', 'NOP Hub', {
    sortable: false,
    minWidthClassName: 'min-w-[240px]',
  }),
  col('status', 'Status', {
    sortable: false,
    minWidthClassName: 'min-w-[130px]',
  }),
  col('caseId', 'Case id', {
    sortable: false,
    minWidthClassName: 'min-w-[120px]',
  }),
  col('isTrial', 'Trial', {
    sortable: false,
    minWidthClassName: 'min-w-[92px]',
  }),
  col('isInMaintenance', 'Maintenance', {
    sortable: false,
    minWidthClassName: 'min-w-[92px]',
  }),
  COMMON_COLUMN_PRESETS.actions,
];

const getCreateActionsMenu = (onEdit: () => void, onDelete?: () => void): MenuItemType[] => [
  {
    key: 'edit-ocs-console',
    type: 'action',
    to: 'edit-ocs-console',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
  ...(onDelete
    ? [
        {
          key: 'delete-ocs-console',
          type: 'action',
          to: 'delete-ocs-console',
          label: (
            <div className="flex items-center gap-2 text-danger">
              <MdOutlineDelete size={16} />
              <span className="text-m-r">Delete</span>
            </div>
          ),
          onAction: () => onDelete(),
        } as MenuItemType,
      ]
    : []),
];

const getEditActionsMenu = (onEdit: () => void): MenuItemType[] => [
  {
    key: 'edit-ocs-console',
    type: 'action',
    to: 'edit-ocs-console',
    label: (
      <div className="flex items-center gap-2">
        <MdOutlineEdit size={16} />
        <span className="text-m-r">Edit</span>
      </div>
    ),
    onAction: () => onEdit(),
  },
];

export const getRenderOcsConsolesTableCell =
  (
    onEdit: (consoleId: string) => void,
    onDelete: (consoleId: string) => void,
    onMaintenanceChange: (ocsConsole: OcsConsoleData) => void,
    onToggleTrialProgram: (ocsConsole: OcsConsoleData) => void,
    hasCreatePermissions: boolean,
    hasEditPermissions: boolean,
    hasDeletePermissions: boolean,
  ) =>
  (ocsConsole: OcsConsoleData, columnKey: Key): ReactNode => {
    const {
      id,
      fullSerialNumber,
      ocsConsoleName,
      caseName,
      organType,
      status,
      hub,
      isInMaintenance,
      logoPath,
      trialProgram,
    } = ocsConsole;
    const colors = getColorsByOrganType(organType);
    const OrganIcon = ORGAN_ICON[organType];

    const onOcsConsoleEdit = () => onEdit(id);
    const onOcsConsoleDelete = () => onDelete(id);

    const ACTIONS_MENU_CREATE = getCreateActionsMenu(
      onOcsConsoleEdit,
      hasDeletePermissions ? onOcsConsoleDelete : undefined,
    );
    const ACTIONS_MENU_EDIT = getEditActionsMenu(onOcsConsoleEdit);

    switch (columnKey) {
      case 'ocsConsoleName':
        return (
          <Link to={`/admin/devices/ocs_consoles/${id}`} className="flex items-center gap-2">
            <TmdxAvatar radius="xl" size="xl" type="device" src={logoPath} />
            <span className="text-m-m">{ocsConsoleName}</span>
          </Link>
        );
      case 'organType':
        return (
          <div
            className={`${colors.bg60} w-6 h-6 flex justify-center items-center rounded-full text-base-5 `}
            data-icon={organType}
          >
            <OrganIcon />
          </div>
        );
      case 'trialProgramName':
        return <span className="text-m-r">{trialProgram ? trialProgram.name : '-'}</span>;
      case 'serialNumber':
        return <span className="text-m-r">{fullSerialNumber}</span>;
      case 'nopHub':
        return <span className="text-m-r">{hub?.name || '-'}</span>;
      case 'status':
        return (
          <TmdxChip
            color={ocsConsoleStatusName[status] === OcsConsoleStatus.Available ? 'success' : 'default'}
            size="sm"
          >
            {ocsConsoleStatusName[status]}
          </TmdxChip>
        );
      case 'caseId':
        return <span className="text-m-r">{caseName || '-'}</span>;
      case 'isTrial':
        return organType !== OrganType.Liver ? (
          <TableSwitch
            isActive={!!trialProgram}
            isDisabled={!hasCreatePermissions && !hasEditPermissions}
            action={() => onToggleTrialProgram(ocsConsole)}
          />
        ) : null;
      case 'isInMaintenance':
        return (
          <TableSwitch
            isActive={isInMaintenance}
            isDisabled={!hasCreatePermissions && !hasEditPermissions}
            action={() => onMaintenanceChange(ocsConsole)}
          />
        );
      case 'actions':
        return (
          <ActionMenu
            placement="left-start"
            items={hasCreatePermissions ? ACTIONS_MENU_CREATE : ACTIONS_MENU_EDIT}
            MenuTrigger={
              <div className="">
                <MdOutlineMoreHoriz size={20} />
              </div>
            }
            label={`${ocsConsoleName} actions menu`}
          />
        );
      default:
        return null;
    }
  };
